"use client";

import { Anchor, Container, SimpleGrid, Stack, Text } from "@mantine/core";
import Image from "next/image";

import { NextLink } from "@/app/lib/components/next/NextLink";
import { DashedBorder } from "@/app/lib/components/ui/DashedBorder";

import classes from "./Footer.module.css";

interface FooterLink {
  label: string;
  href: string;
}

interface FooterColumn {
  heading: string;
  links: FooterLink[];
}

const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "Platform", href: "/platform" },
      { label: "Pricing", href: "/pricing" },
      { label: "Integrations", href: "/integrations" },
      { label: "Security", href: "/security" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Resources", href: "/resources" },
      { label: "Use Cases", href: "/use-cases" },
      { label: "Frameworks", href: "/decision-making-frameworks" },
      { label: "Support", href: "/support" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Subprocessors", href: "/subprocessors" },
      { label: "DPA", href: "/dpa" },
    ],
  },
];

export default function Footer() {
  return (
    <footer>
      <DashedBorder />
      {/* Below md the copyright row is last, so the fixed AudiencePill lands on
          its empty right half rather than on the Legal links a row above. */}
      <Container size="lg" pt={{ base: "xl", md: "2xl" }} pb="2xl">
        <div className={classes.grid}>
          <Stack gap="md" maw={240} className={classes.brand}>
            {/* The SVG viewBox is the artwork's ink bounds, so these dimensions
                are the rendered logo size with no built-in clear space. */}
            <Image height={24} width={111} src="/logo/logo.svg" alt="Orgtools Logo" />
            <Text size="sm" c="dimmed" lh="lg">
              A founder-mode operating model for your people and agents.
            </Text>
          </Stack>

          {/* Two-up below sm: lg between the column rows keeps the three groups
              reading as one block while still separating them from each other. */}
          <SimpleGrid
            className={classes.links}
            cols={{ base: 2, sm: 3 }}
            spacing={{ base: "xl", md: "3xl" }}
            verticalSpacing="lg"
          >
            {FOOTER_COLUMNS.map((column) => (
              <Stack key={column.heading} gap="xs">
                <Text
                  size="xs"
                  fw={600}
                  tt="uppercase"
                  c="gray.6"
                  style={{ letterSpacing: "0.06em" }}
                >
                  {column.heading}
                </Text>
                {column.links.map((link) => (
                  <Anchor
                    key={link.href}
                    component={NextLink}
                    href={link.href}
                    size="sm"
                    c="gray.7"
                  >
                    {link.label}
                  </Anchor>
                ))}
              </Stack>
            ))}
          </SimpleGrid>

          <Text size="sm" c="dimmed" className={classes.legal}>
            © {new Date().getFullYear()} Orgtools
          </Text>
        </div>
      </Container>
    </footer>
  );
}
